/**
 * ClaimsLabelFilter - a label filter dropdown for the claims list toolbar.
 *
 * Renders a filter icon button (sits to the right of Export) that opens a
 * checkbox list of the available triage labels. Checking labels filters
 * the claims list to claims tagged with ANY of them.
 */

import { Filter } from 'lucide-react'
import { Popover } from '@base-ui/react/popover'
import { Checkbox } from '@/shared/components/ui/checkbox'
import type { ClaimLabelOption } from '@/admin/hooks/use-claims'

const HEX6 = /^#[0-9a-fA-F]{6}$/

export function ClaimsLabelFilter({
  options,
  selected,
  onChange,
}: {
  options: ClaimLabelOption[]
  selected: string[]
  onChange: (next: string[]) => void
}) {
  if (options.length === 0) return null

  const set = new Set(selected)
  const toggle = (value: string) =>
    onChange(set.has(value) ? selected.filter((v) => v !== value) : [...selected, value])

  return (
    <Popover.Root>
      <Popover.Trigger
        title="Filter by label"
        className="relative inline-flex cursor-pointer items-center gap-1.5 rounded-[6px] border-[0.5px] border-solid border-[var(--rule-strong)] px-2.5 py-1.5 text-[11px] uppercase tracking-[0.1em] text-[var(--ink-2)] transition-colors duration-150 hover:border-[var(--ink-3)] data-[popup-open]:border-[var(--ink-3)]"
      >
        <Filter className="h-3 w-3" />
        {selected.length > 0 ? (
          <span className="inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-[var(--green)] px-1 text-[9px] font-medium text-white">
            {selected.length}
          </span>
        ) : null}
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Positioner side="bottom" align="end" sideOffset={6} className="isolate z-50">
          <Popover.Popup className="min-w-[200px] rounded-[8px] border-[0.5px] border-[var(--rule-strong)] bg-[var(--bg)] p-1.5 shadow-[var(--shadow-float)]">
            <div className="flex items-center justify-between px-2 py-1">
              <span className="clara-label text-[var(--ink-3)]">Filter by label</span>
              {selected.length > 0 ? (
                <button
                  type="button"
                  className="text-[11px] text-[var(--ink-3)] hover:text-[var(--ink)]"
                  onClick={() => onChange([])}
                >
                  Clear
                </button>
              ) : null}
            </div>
            {options.map((opt) => {
              const c = opt.color && HEX6.test(opt.color) ? opt.color : null
              return (
                <label
                  key={opt.value}
                  className="flex cursor-pointer items-center gap-2 rounded-[4px] px-2 py-1.5 text-[13px] text-[var(--ink)] hover:bg-[var(--bg-light)]"
                >
                  <Checkbox
                    checked={set.has(opt.value)}
                    onCheckedChange={() => toggle(opt.value)}
                  />
                  {c ? (
                    <span
                      className="h-2 w-2 shrink-0 rounded-full"
                      style={{ backgroundColor: c }}
                    />
                  ) : null}
                  {opt.label}
                </label>
              )
            })}
          </Popover.Popup>
        </Popover.Positioner>
      </Popover.Portal>
    </Popover.Root>
  )
}
